/**
 * Rules-mode provision extractor.
 *
 * Build doc §10 describes the AI extraction pipeline (Claude Sonnet, tool-use,
 * Zod-validated). This module is the deterministic sibling that ships first:
 * it parses the section structure of a side letter, classifies each section
 * against the locked taxonomy (keyword scoring), and parses structured values
 * (fee percentages, thresholds, frequencies, MFN scope) out of the text.
 *
 * Contract notes:
 *  - `rawText` is always a verbatim substring of the input (the hallucination
 *    guard in §10.7 holds by construction here; `verifyVerbatim` re-checks).
 *  - Confidence caps at 0.95 — rules mode never claims certainty; sub-0.6
 *    renders red in the review UI and demands human action.
 *  - Pages are delimited by form-feed (`\f`) characters in the input text;
 *    the client PDF parser inserts them between pages.
 */

import { CATEGORY_PATTERNS, type ProvisionCategory } from "./taxonomy";
import type { ExtractedProvision, ExtractionResult, StructuredValue } from "./types";

export const RULES_ENGINE_VERSION = "rules-v1.0";

const MIN_SECTION_CHARS = 60;

interface Section {
  text: string;
  page: number;
}

/** Split source text into candidate provision sections. */
export function segmentSections(text: string): Section[] {
  const headingRe = /^[ \t]{0,6}(?:Section\s+)?\d{1,2}[.)][ \t]+/gm;
  const starts: number[] = [];
  for (const m of text.matchAll(headingRe)) {
    if (m.index !== undefined) starts.push(m.index);
  }

  const sections: Section[] = [];
  const pageOf = (offset: number): number => (text.slice(0, offset).match(/\f/g)?.length ?? 0) + 1;

  if (starts.length >= 2) {
    for (let i = 0; i < starts.length; i++) {
      const start = starts[i];
      if (start === undefined) continue;
      const end = starts[i + 1] ?? text.length;
      const body = text.slice(start, end).trim();
      if (body.length >= MIN_SECTION_CHARS) sections.push({ text: body, page: pageOf(start) });
    }
    return sections;
  }

  // Fallback: blank-line paragraphs (letters without numbered sections).
  let offset = 0;
  for (const para of text.split(/\n[ \t]*\n/)) {
    const idx = text.indexOf(para, offset);
    offset = idx + para.length;
    const body = para.trim();
    if (body.length >= MIN_SECTION_CHARS) sections.push({ text: body, page: pageOf(idx) });
  }
  return sections;
}

interface Classification {
  category: ProvisionCategory;
  score: number;
}

export function classifySection(sectionText: string): Classification {
  // Letters hard-wrap lines mid-phrase; multi-word patterns must not care.
  const lower = sectionText.toLowerCase().replace(/\s+/g, " ");
  let best: Classification = { category: "other", score: 0 };
  for (const [category, patterns] of Object.entries(CATEGORY_PATTERNS)) {
    let score = 0;
    for (const { pattern, weight } of patterns) {
      if (pattern.test(lower)) score += weight;
    }
    if (score > best.score) best = { category: category as ProvisionCategory, score };
  }
  // Below this, the keyword evidence is too thin to trust a label.
  if (best.score < 3) return { category: "other", score: best.score };
  return best;
}

// ---------------------------------------------------------------------------
// Value parsers
// ---------------------------------------------------------------------------

/** Parse "$50,000,000", "US$50 million", "$25m" → USD number. */
export function parseMoney(text: string): number | null {
  const m = /(?:US)?\$\s?([\d,]+(?:\.\d+)?)\s*(billion|million|bn|mm|m\b)?/i.exec(text);
  if (m?.[1] === undefined) return null;
  let value = Number(m[1].replaceAll(",", ""));
  if (Number.isNaN(value)) return null;
  const unit = m[2]?.toLowerCase();
  if (unit === "billion" || unit === "bn") value *= 1_000_000_000;
  else if (unit === "million" || unit === "mm" || unit === "m") value *= 1_000_000;
  return value;
}

function parsePctNear(text: string, anchor: RegExp): number | null {
  // Look for a percentage within the sentence(s) mentioning the anchor.
  for (const sentence of text.split(/(?<=[.;])\s+/)) {
    if (anchor.test(sentence.toLowerCase())) {
      const m = /(\d{1,2}(?:\.\d+)?)\s?%/.exec(sentence);
      if (m) return Number(m[1]);
    }
  }
  const m = /(\d{1,2}(?:\.\d+)?)\s?%/.exec(text);
  return m ? Number(m[1]) : null;
}

export function parseFrequency(text: string): StructuredValue["frequency"] | null {
  const lower = text.toLowerCase();
  if (/semi[- ]annual/.test(lower)) return "semi_annual";
  if (/quarterly|each quarter|per quarter/.test(lower)) return "quarterly";
  if (/annual(?:ly)?|each year|per (?:calendar )?year/.test(lower)) return "annual";
  if (lower.includes("monthly")) return "quarterly"; // calendar floor: we track monthly duties at quarterly cadence in MVP
  if (/upon|in the event|following any/.test(lower)) return "on_event";
  return null;
}

const EXCLUSION_CATEGORY_HINTS: { pattern: RegExp; category: ProvisionCategory }[] = [
  {
    pattern: /regulatory|erisa|public plan|freedom of information|foia/,
    category: "regulatory_rep",
  },
  { pattern: /tax/, category: "tax_rep" },
  { pattern: /co[- ]invest/, category: "co_invest" },
  { pattern: /advisory committee|lpac|observer/, category: "advisory_committee" },
  { pattern: /transfer/, category: "transfer" },
  { pattern: /confidentialit/, category: "confidentiality" },
];

function parseMfnDetails(text: string): StructuredValue {
  // Hard line wraps in letters split phrases ("equal\nto or less than") — collapse.
  const lower = text.toLowerCase().replace(/\s+/g, " ");
  const value: StructuredValue = {};

  // Scope
  if (
    /(?:solely|only)[^.]*(?:economic|fees?|carried interest|financial terms)/.test(lower) ||
    /economic (?:terms|provisions|rights) only/.test(lower)
  ) {
    value.mfn_scope = "economic_only";
  } else {
    value.mfn_scope = "full";
  }

  // Size-based election (only terms of equal-or-smaller LPs can be elected)
  value.mfn_size_based =
    /commitment[^.]*(?:equal to or (?:less|lower) than|does not exceed|not (?:greater|larger) than|smaller than)/.test(
      lower,
    );

  // Operative threshold on the holder's own commitment
  const thresholdSentence = lower
    .split(/(?<=[.;])\s+/)
    .find((s) => /(?:at least|not less than|minimum)/.test(s) && s.includes("commitment"));
  if (thresholdSentence) {
    const money = parseMoney(thresholdSentence);
    if (money) value.threshold_usd = money;
  }

  // Excluded categories
  const exclusionSentences = lower
    .split(/(?<=[.;])\s+/)
    .filter((s) =>
      /shall not apply|not apply to|other than|excluding|except (?:for|with respect to)/.test(s),
    );
  const excluded = new Set<ProvisionCategory>();
  for (const sentence of exclusionSentences) {
    for (const { pattern, category } of EXCLUSION_CATEGORY_HINTS) {
      if (pattern.test(sentence)) excluded.add(category);
    }
  }
  if (excluded.size > 0) value.mfn_excluded_categories = [...excluded];

  // Cross-fund reach
  if (lower.includes("successor fund")) value.mfn_fund_scope = "successor_funds";
  else if (/any other (?:fund|vehicle|account)s? (?:managed|advised|sponsored)/.test(lower))
    value.mfn_fund_scope = "manager_funds";
  else value.mfn_fund_scope = "this_fund_only";

  return value;
}

function parseStructuredValue(category: ProvisionCategory, text: string): StructuredValue | null {
  switch (category) {
    case "mgmt_fee_discount": {
      const fee = parsePctNear(text, /management fee|reduced to/);
      return fee !== null ? { fee_pct: fee } : null;
    }
    case "carry_modification": {
      const carry = parsePctNear(text, /carried interest|carry/);
      const hurdle = parsePctNear(text, /hurdle|preferred return/);
      const v: StructuredValue = {};
      if (carry !== null) v.carry_pct = carry;
      if (hurdle !== null && hurdle !== carry) v.hurdle_pct = hurdle;
      return Object.keys(v).length ? v : null;
    }
    case "expense_cap":
    case "org_expense_cap": {
      const cap = parseMoney(text);
      const pct = parsePctNear(text, /expenses/);
      const v: StructuredValue = {};
      // 2026-07-03 audit fix (S1-2): a percentage-of-commitment cap ("shall
      // not exceed 0.15% of the Investor's Commitment") used to be written
      // into `fee_pct` — the SAME field mgmt_fee_discount uses for the
      // management-fee rate. mfn.ts's numericallyBetter only ever read
      // `cap_usd` for this category, so a %-based cap silently never
      // compared and a genuinely-better percentage cap could be suppressed
      // by an unrelated dollar-based holder cap. Use the dedicated
      // `cap_pct` field instead so the two representations never collide.
      if (cap !== null) v.cap_usd = cap;
      else if (pct !== null) v.cap_pct = pct;
      return Object.keys(v).length ? v : null;
    }
    case "mfn":
    case "most_favored_nation_express":
      return parseMfnDetails(text);
    case "reporting_freq":
    case "esg":
    case "transparency":
    case "tax_rep": {
      const frequency = parseFrequency(text);
      return frequency ? { frequency } : null;
    }
    case "co_invest": {
      const threshold = parseMoney(text);
      return threshold !== null ? { threshold_usd: threshold } : null;
    }
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Summaries
// ---------------------------------------------------------------------------

function pct(n: number): string {
  return `${n}%`;
}

function usd(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(n % 1_000_000_000 ? 1 : 0)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  return `$${n.toLocaleString("en-US")}`;
}

function summarize(category: ProvisionCategory, value: StructuredValue | null): string {
  switch (category) {
    case "mgmt_fee_discount":
      return value?.fee_pct !== undefined
        ? `Management fee reduced to ${pct(value.fee_pct)} per annum for this LP.`
        : "Management fee concession granted to this LP.";
    case "carry_modification": {
      const parts: string[] = [];
      if (value?.carry_pct !== undefined)
        parts.push(`carried interest set at ${pct(value.carry_pct)}`);
      if (value?.hurdle_pct !== undefined)
        parts.push(`preferred return of ${pct(value.hurdle_pct)}`);
      return parts.length
        ? `Carry economics modified: ${parts.join(", ")}.`
        : "Carried interest economics modified for this LP.";
    }
    case "gp_commitment":
      return "GP commitment requirement or carve-out agreed with this LP.";
    case "expense_cap":
      if (value?.cap_usd !== undefined)
        return `Fund expenses capped at ${usd(value.cap_usd)} for allocation purposes.`;
      if (value?.cap_pct !== undefined)
        return `Fund expenses capped at ${pct(value.cap_pct)} of commitment for allocation purposes.`;
      return "Cap or carve-out on fund expenses for this LP.";
    case "org_expense_cap":
      if (value?.cap_usd !== undefined)
        return `Organizational expenses capped at ${usd(value.cap_usd)}.`;
      if (value?.cap_pct !== undefined)
        return `Organizational expenses capped at ${pct(value.cap_pct)} of commitment.`;
      return "Cap on organizational expenses.";
    case "mfn":
    case "most_favored_nation_express": {
      const bits: string[] = [];
      bits.push(value?.mfn_scope === "economic_only" ? "Economic-terms-only MFN" : "Full MFN");
      if (value?.threshold_usd) bits.push(`operative above ${usd(value.threshold_usd)} commitment`);
      if (value?.mfn_size_based) bits.push("limited to terms of equal-or-smaller LPs");
      if (value?.mfn_excluded_categories?.length)
        bits.push(
          `excluding ${value.mfn_excluded_categories.length} categor${value.mfn_excluded_categories.length === 1 ? "y" : "ies"}`,
        );
      return `${bits.join(", ")}.`;
    }
    case "transparency":
      return "Enhanced information or inspection rights granted to this LP.";
    case "advisory_committee":
      return "LPAC seat or observer rights granted to this LP.";
    case "key_person":
      return "Key person provision: triggers and consequences defined for named principals.";
    case "reporting_freq":
      return value?.frequency
        ? `Custom ${value.frequency.replace("_", "-")} reporting obligation to this LP.`
        : "Custom reporting obligation to this LP.";
    case "co_invest":
      return "Co-investment rights granted to this LP.";
    case "redemption":
      return "Redemption or withdrawal rights for this LP.";
    case "transfer":
      return "Transfer rights or consent standards modified for this LP.";
    case "regulatory_rep":
      return "Regulatory representation or accommodation (e.g. ERISA, public-plan, FOIA).";
    case "tax_rep":
      return "Tax representation, covenant, or reporting accommodation.";
    case "structural":
      return "Structural accommodation (parallel vehicle, AIV, feeder).";
    case "esg":
      return value?.frequency
        ? `ESG reporting obligation (${value.frequency.replace("_", "-")}) or investment exclusions.`
        : "ESG covenant, reporting obligation, or exclusions.";
    case "sanctions":
      return "Sanctions / AML representation.";
    case "confidentiality":
      return "Confidentiality terms modified for this LP.";
    case "indemnity":
      return "Indemnification or exculpation carve-out for this LP.";
    case "dispute":
      return "Dispute resolution / governing law override.";
    case "other":
      return "Unclassified provision — needs human categorization.";
  }
}

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

export function extractProvisions(text: string): ExtractionResult {
  const warnings: string[] = [];
  const sections = segmentSections(text);
  if (sections.length === 0) {
    warnings.push("No provision-like sections found — document may be scanned or empty.");
  }

  const provisions: ExtractedProvision[] = [];
  for (const section of sections) {
    const { category, score } = classifySection(section.text);
    // Letterhead/signature blocks classify as 'other' with score 0 — skip pure noise,
    // keep genuine-but-unclassifiable sections so a human can look (build doc §10.3).
    if (
      category === "other" &&
      score === 0 &&
      !/agree|shall|hereby/.test(section.text.toLowerCase())
    ) {
      continue;
    }
    const structuredValue = parseStructuredValue(category, section.text);
    const confidence =
      category === "other"
        ? 0.4
        : Math.min(0.95, 0.5 + 0.08 * Math.min(score, 5) + (structuredValue ? 0.05 : 0));

    provisions.push({
      category,
      rawText: section.text,
      summary: summarize(category, structuredValue),
      structuredValue,
      pageNumber: section.page,
      confidence: Math.round(confidence * 100) / 100,
    });
  }

  return { provisions, engine: RULES_ENGINE_VERSION, warnings };
}

/**
 * Hallucination guard (build doc §10.7): every rawText must appear verbatim in
 * the source. Rules mode satisfies this by construction; the Claude path runs
 * the same check before anything is persisted.
 */
export function verifyVerbatim(sourceText: string, provisions: ExtractedProvision[]): number[] {
  const bad: number[] = [];
  provisions.forEach((p, i) => {
    if (!sourceText.includes(p.rawText)) bad.push(i);
  });
  return bad;
}
